// Third-party imports
import { useMediaQuery } from 'react-responsive';

// Types
import Meta from 'model/Service';

// UI Components
import { Table } from 'components/UI/V3/Table/Table';
import { TableHeader } from 'components/UI/V3/Table/Header/TableHeader';
import { TableHeaderCell } from 'components/UI/V3/Table/HeaderCell/TableHeaderCell';
import { TableBody } from 'components/UI/V3/Table/Body/TableBody';
import Spinner from 'components/UI/V3/Spinner/Spinner';

// Local Components
import PromoCodesIsEmpty from '../List/PromoCodesIsEmpty';
import PromoCodesUsageRow from './PromoCodesUsageRow';

// Styles
import styles from './PromoCodesUsageTable.module.scss';
import { Invoice } from '@spike/promocode-model';
import { PaginationProps } from 'components/UI/V3/Pagination/Pagination';
import { usePromoCodes } from '../PromoCodesProvider';

interface PromoCodesUsageTableProps {
    redeems: Invoice[];
    isLoading?: boolean;
    hasInitialized?: boolean;
    isEmpty?: boolean;
    onViewBooking?: (bookingId: number, orderId: number) => void;
    onViewInvoice?: (invoice: Invoice) => void;
    className?: string;
}
const PromoCodeUsageTable = ({
    redeems,
    isLoading,
    hasInitialized,
    isEmpty,
    onViewBooking,
    onViewInvoice,
    className
}: PromoCodesUsageTableProps) => {
    const isMobile = useMediaQuery({ query: '(max-width:768px)' });

    const { redeemsPage, redeemsTotalPages, redeemsSize, setRedeemsSize, setRedeemsPage } = usePromoCodes();

    const pagination: PaginationProps = {
        page: redeemsPage,
        totalPages: redeemsTotalPages,
        size: redeemsSize,
        isLoading
    };

    // RENDER DIFFERENT TABLE BODIES BASED ON STATE
    const renderTableContent = () => {
        // Show spinner in TableBody when:
        // 1. First load (no data yet)
        // 2. Loading with no current data to show (empty results + loading)
        const shouldShowSpinnerInBody =
            isLoading &&
            (!hasInitialized || // First time loading
                redeems.length === 0); // Currently no data to show while loading

        if (shouldShowSpinnerInBody) {
            return (
                <TableBody>
                    <tr>
                        <td
                            colSpan={5}
                            className={styles.loadingCell}
                        >
                            <div className={styles.spinnerContainer}>
                                <Spinner />
                            </div>
                        </td>
                    </tr>
                </TableBody>
            );
        }

        // No results for current filters/search
        // Note: "Truly empty" case is handled at component level (parent), not here
        if (isEmpty) {
            return (
                <TableBody>
                    <tr>
                        <td
                            colSpan={5}
                            className={styles.emptyCell}
                        >
                            <PromoCodesIsEmpty
                                title="No redemptions yet"
                                text="Once someone uses this promo code, they'll appear here."
                            />
                        </td>
                    </tr>
                </TableBody>
            );
        }

        // Normal data
        return (
            <TableBody>
                {redeems.map((redeem, index) => (
                    <PromoCodesUsageRow
                        key={redeem.id}
                        redeem={redeem}
                        isLast={redeems.length === index + 1}
                        isMobile={isMobile}
                        onViewBooking={onViewBooking && onViewBooking}
                        onViewInvoice={onViewInvoice && onViewInvoice}
                    />
                ))}
            </TableBody>
        );
    };
    return (
        <Table
            pagination={pagination}
            onPageChange={setRedeemsPage}
            onSizeChange={setRedeemsSize}
            className={className}
            fullHeight={true}
            firstColumnFixed={isMobile}
        >
            <TableHeader className={styles.tableHeader}>
                <TableHeaderCell>Client</TableHeaderCell>
                <TableHeaderCell>Pets</TableHeaderCell>
                <TableHeaderCell>Date</TableHeaderCell>
                <TableHeaderCell>Channel</TableHeaderCell>
                <TableHeaderCell>Total</TableHeaderCell>
                <TableHeaderCell></TableHeaderCell>
            </TableHeader>

            {renderTableContent()}
        </Table>
    );
};

export default PromoCodeUsageTable;
